import {getDatadogRum} from '../../../../util/datadog/datadogRum';
import * as Run from '../../../../util/runs';
import {fromJson} from '../../../../util/runs';
import {detectZeroLengthHistories} from '../../alerts';
import {type BucketedLine} from '../../types';
import {
  type BackendLinesDeltaQueryQuery,
  type GroupedBackendLinesDeltaQueryQuery,
} from './../../../../generated/graphql';
import {type QueryVars} from './queryVars';
import {
  type BucketedQueryRunsDeltas,
  type GroupedBucketedQueryRunWithGroupedRunConnection,
  type WithBaselinePinnedFlags,
} from './types';
import {profileRenderTimeSaved, updateRunFlags} from './utils';

const DD_RUM = getDatadogRum();

export interface ApplyDeltaResult {
  runsById: Record<string, BucketedLine>;
  order: string[];
}

export function applyBackendLinesDelta({
  currentRunsById,
  delta,
  order,
  runFlags,
}: {
  currentRunsById: Record<string, BucketedLine>;
  order: string[];
  delta: NonNullable<
    NonNullable<
      NonNullable<BackendLinesDeltaQueryQuery['project']>['runs']
    >['deltas']
  >['delta'];
  runFlags?: Record<string, {isBaseline: boolean; isPinned: boolean}>;
}): ApplyDeltaResult {
  const deleteOps = Object.keys(currentRunsById).filter(
    rId => !order.includes(rId)
  );

  // Check if we need to do any work at all
  const hasDeltaWork = delta.length > 0 || deleteOps.length > 0;
  const hasFlagWork = runFlags && Object.keys(runFlags).length > 0;

  if (!hasDeltaWork && !hasFlagWork) {
    return {runsById: currentRunsById, order};
  }

  if (!hasDeltaWork) {
    // Only flag updates, no deltas or deletions
    const updatedRunsById = updateRunFlags(currentRunsById, runFlags);
    return {runsById: updatedRunsById, order};
  }

  // Work with a temporary object for all updates
  const tempRunsById = Object.assign({}, currentRunsById);

  deleteOps.forEach(rId => {
    delete tempRunsById[rId];
  });

  delta.forEach(runDelta => {
    if (!runDelta.run) {
      DD_RUM.addError(new Error('backend lines error'), {
        reason: 'Bucketed Delta Op returned without valid run',
      });
      return;
    }

    try {
      const parsedRun = Run.fromJson(runDelta.run);
      if (parsedRun == null) {
        DD_RUM.addError(new Error('backend lines error'), {
          reason: 'Unable to parse run from server',
          runId: runDelta.run.id,
          displayName: runDelta.run.displayName,
        });
        return;
      }

      // Update the entire record for any diffOp
      // isBaseline/isPinned are added client-side by mergeBaselineAndPinnedRuns
      const runWithAddlFlags = runDelta.run as WithBaselinePinnedFlags<
        typeof runDelta.run
      >;

      tempRunsById[runDelta.run.id] = {
        ...parsedRun,
        history: [], // when we use lines zero out the history
        historyLines: runDelta.run.lines,
        isBaseline: runWithAddlFlags.isBaseline,
        isPinned: runWithAddlFlags.isPinned,
      };
    } catch (e) {
      DD_RUM.addError(new Error('backend lines error'), {
        reason: 'Error parsing run data when applying delta',
      });
    }
  });

  // Update flags for runs that didn't have deltas
  if (runFlags) {
    for (const [runId, flags] of Object.entries(runFlags)) {
      const existingRun = tempRunsById[runId];
      if (
        existingRun &&
        (existingRun.isBaseline !== flags.isBaseline ||
          existingRun.isPinned !== flags.isPinned)
      ) {
        tempRunsById[runId] = {
          ...existingRun,
          isBaseline: flags.isBaseline,
          isPinned: flags.isPinned,
        };
      }
    }
  }

  // Rebuild runsById in the correct order from the API's order array.
  // This ensures that object key iteration order matches the sort order
  // specified by the server, f
  const orderedRunsById: Record<string, BucketedLine> = {};
  order.forEach(rId => {
    if (tempRunsById[rId]) {
      orderedRunsById[rId] = tempRunsById[rId];
    }
  });

  profileRenderTimeSaved(orderedRunsById, 'backend');
  return {runsById: orderedRunsById, order};
}

export function applyGroupedBackendLinesDelta({
  currentRunsById,
  delta,
  order,
}: {
  currentRunsById: Record<string, BucketedLine>;
  order: string[];
  delta: NonNullable<
    NonNullable<
      NonNullable<GroupedBackendLinesDeltaQueryQuery['project']>['groupedRuns']
    >['deltas']
  >['delta'];
}): ApplyDeltaResult {
  const deleteOps = Object.keys(currentRunsById).filter(
    rId => !order.includes(rId)
  );
  if (delta.length === 0 && deleteOps.length === 0) {
    return {runsById: currentRunsById, order};
  }

  // Work with a temporary object for all updates
  const tempRunsById = Object.assign({}, currentRunsById);
  deleteOps.forEach(rId => {
    delete tempRunsById[rId];
  });

  delta.forEach(runDelta => {
    if (runDelta.__typename !== 'GroupBucketDiff') {
      // This should never happen because we check the typename before calling this function,
      // but it's required for type safety
      DD_RUM.addError(new Error('backend lines error'), {
        reason: 'Grouped Bucketed Delta Op called on non-grouped run',
        runId: runDelta.run?.id,
      });
      return;
    }
    if (!runDelta.groupedRun) {
      DD_RUM.addError(new Error('backend lines error'), {
        reason: 'Grouped Bucketed Delta Op returned without valid run',
      });
      return;
    }
    // RunFromGraphQL doesn't allow null for createdAt
    const groupedRun = {
      ...runDelta.groupedRun,
      createdAt: runDelta.groupedRun.createdAt ?? undefined,
    };

    try {
      const parsedRun = Run.fromJson(groupedRun);
      if (parsedRun == null) {
        DD_RUM.addError(new Error('backend lines error'), {
          reason: 'Unable to parse grouped run from server',
          groupedRunId: groupedRun.id, // Note: This is only the ID of one of the runs in the group
          displayName: groupedRun.displayName,
        });
        return;
      }

      // Update the entire record for any diffOp
      tempRunsById[groupedRun.id] = {
        ...parsedRun,
        history: [], // when we use lines zero out the history
        historyLines: groupedRun.lines,
      };
    } catch (e) {
      DD_RUM.addError(new Error('backend lines error'), {
        reason: 'Error applying grouped backend lines delta',
        error: e instanceof Error ? e.message : 'Unknown error',
        groupedRunId: groupedRun.id,
        displayName: groupedRun.displayName,
      });
    }
  });

  // Rebuild runsById in the correct order from the API's order array.
  // This ensures that object key iteration order matches the sort order
  // specified by the server
  const orderedRunsById: Record<string, BucketedLine> = {};
  order.forEach(rId => {
    if (tempRunsById[rId]) {
      orderedRunsById[rId] = tempRunsById[rId];
    }
  });

  profileRenderTimeSaved(orderedRunsById, 'grouped');
  return {runsById: orderedRunsById, order};
}

export function applyDelta({
  currentRunsById,
  delta,
  order,
  specs,
  runFlags,
}: {
  currentRunsById: Record<string, BucketedLine>;
  order: string[];
  delta: BucketedQueryRunsDeltas;
  specs: QueryVars['bucketedHistorySpecs'];
  runFlags?: Record<string, {isBaseline: boolean; isPinned: boolean}>;
}): ApplyDeltaResult {
  const newRunsById = delta.reduce(
    (acc, runDelta) => {
      const parsedRun = fromJson(runDelta.run);
      if (parsedRun == null || runDelta.run == null) {
        DD_RUM.addError(new Error('Runs_line_plot_error'), {
          reason: 'Unable to apply delta',
          runId: runDelta.run?.id,
          displayName: runDelta.run?.displayName,
        });
        return acc;
      }

      detectZeroLengthHistories({
        histories: runDelta.run?.bucketedHistory ?? [],
        id: runDelta.run.id,
        displayName: runDelta.run.displayName ?? '',
        specs,
      });

      acc[runDelta.run.id] = {
        ...parsedRun,
        history: runDelta.run?.bucketedHistory ?? [],
      };
      return acc;
    },
    {} as Record<string, BucketedLine>
  );

  /**
   * IMPORTANT: Object key insertion order matters here!
   * We intentionally rebuild runsById with keys in the order specified by the API's
   * `order` array. This ensures that downstream code using Object.values() or
   * Object.keys() will iterate in the correct sort order.
   *
   * TODO: This relies on JavaScript's implicit object key iteration order, which is
   * fragile. The proper fix is to return {runsById, order} and have consumers
   * explicitly use the order array rather than relying on object key order.
   */
  const runsById: Record<string, BucketedLine> = {};
  order.forEach(rId => {
    const run = newRunsById[rId] ?? currentRunsById[rId];

    // Update flags if they exist and differ from current values
    if (run && runFlags?.[rId]) {
      const flags = runFlags[rId];
      if (
        run.isBaseline !== flags.isBaseline ||
        run.isPinned !== flags.isPinned
      ) {
        // Only clone if flags actually changed
        runsById[rId] = {
          ...run,
          isBaseline: flags.isBaseline,
          isPinned: flags.isPinned,
        };
      } else {
        runsById[rId] = run;
      }
    } else {
      runsById[rId] = run;
    }
  });

  return {runsById, order};
}

// Narrowed deltas type specifically for the GroupedRunConnection branch so array elements
// are not inferred as any when reducing over union-shaped arrays
export type GroupedBucketedRunDeltas = NonNullable<
  NonNullable<GroupedBucketedQueryRunWithGroupedRunConnection['deltas']>
>['delta'];

export function applyGroupedDelta({
  currentRunsById,
  delta,
  order,
  specs,
}: {
  currentRunsById: Record<string, BucketedLine>;
  order: string[];
  delta: GroupedBucketedRunDeltas;
  specs: QueryVars['bucketedHistorySpecs'];
}): ApplyDeltaResult {
  const newRunsById = delta.reduce(
    (acc, runDelta) => {
      if (runDelta.__typename !== 'GroupBucketDiff') {
        DD_RUM.addError(new Error('Runs_line_plot_error'), {
          reason: 'Grouped Bucketed Delta Op called on non-grouped run',
          typename: runDelta.__typename,
        });
        return acc;
      }
      if (!runDelta.groupedRun) {
        DD_RUM.addError(new Error('Runs_line_plot_error'), {
          reason: 'Grouped Bucketed Delta Op returned without groupedRun',
        });
        return acc;
      }
      // RunFromGraphQL doesn't allow null for createdAt
      const groupedRun = {
        ...runDelta.groupedRun,
        createdAt: runDelta.groupedRun.createdAt ?? undefined,
      };

      const parsedRun = fromJson(groupedRun);
      if (parsedRun == null) {
        DD_RUM.addError(new Error('Runs_line_plot_error'), {
          reason: 'Unable to apply grouped delta',
          groupedRunId: groupedRun.id, // Note: This is only the ID of one of the runs in the group
          displayName: groupedRun.displayName,
        });
        return acc;
      }

      detectZeroLengthHistories({
        histories: runDelta.groupedRun?.bucketedHistory ?? [],
        id: runDelta.groupedRun.id,
        displayName: runDelta.groupedRun.displayName ?? '',
        specs,
      });
      acc[runDelta.groupedRun.id] = {
        ...parsedRun,
        history: runDelta.groupedRun?.bucketedHistory ?? [],
      };
      return acc;
    },
    {} as Record<string, BucketedLine>
  );

  /**
   * IMPORTANT: Object key insertion order matters here!
   * We intentionally rebuild runsById with keys in the order specified by the API's
   * `order` array. This ensures that downstream code using Object.values() or
   * Object.keys() will iterate in the correct sort order.
   *
   * TODO: This relies on JavaScript's implicit object key iteration order, which is
   * fragile. The proper fix is to return {runsById, order} and have consumers
   * explicitly use the order array rather than relying on object key order.
   */
  const runsById: Record<string, BucketedLine> = {};
  order.forEach(rId => {
    runsById[rId] = newRunsById[rId] ?? currentRunsById[rId];
  });

  return {runsById, order};
}
